import React from 'react';
import { Button } from 'baseui/button';
import { StatefulPopover, PLACEMENT } from 'baseui/popover';
import { useStyletron } from 'baseui';
import { CustomTheme } from '../../baseweb-custom-theme/customTheme';
import { Block } from 'baseui/block';
import { ChevronRight, Check } from 'baseui/icon';
import { FilterLaunchedByProps } from '../../relay-messenger-list/RelayMessenger.list';
import { FilterMethods } from '../../relay-messenger-conversations/RelayMessengerConversations';
import { FilterVariantButton } from './FilterVariantButton';
import { useLaunchedBy } from '@app/two-way/relay-messenger/context/LaunchedByListContext';

interface FilterLaunchedByItemProps {
  filterLaunchedBy: FilterLaunchedByProps;
  close: () => void;
  clearFilterState: () => void;
  clearSortingState: () => void;
}

export const FilterLaunchedByAction = ({
  filterLaunchedBy,
  close,
  clearFilterState,
  clearSortingState,
}: FilterLaunchedByItemProps) => {
  const [css, theme] = useStyletron();
  const customTheme = theme as CustomTheme;
  const { isLaunchedByError, launchedByList } = useLaunchedBy();

  return (
    <StatefulPopover
      content={() => (
        <Block
          overrides={{
            Block: {
              style: {
                width: '300px',
                maxHeight: '600px',
                overflowY: 'scroll',
                display: 'flex',
                flexDirection: 'column',
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px',
                '::-webkit-scrollbar': {
                  width: '8px',
                },
                '::-webkit-scrollbar-thumb': {
                  backgroundColor: '#c1c1c1',
                  borderRadius: '8px',
                },
                '::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                },
              },
            },
          }}
        >
          {launchedByList &&
            launchedByList.map((launchedBy, index) => (
              <FilterVariantButton
                key={index}
                value={launchedBy.lbName}
                filterOptionClicked={
                  filterLaunchedBy?.filterOptionLaunchedByClicked
                }
                filterByOption={
                  filterLaunchedBy?.channelsFilterOptions.launched_by.find(
                    (lb) => lb === launchedBy.lbName,
                  )
                    ? launchedBy.lbName
                    : null
                }
                setFilterByOption={(option: any) => {
                  filterLaunchedBy?.setChannelsFilterOptions((prevState) => ({
                    ...prevState,
                    launched_by: [...prevState.launched_by, option],
                  }));
                }}
                close={close}
                dataTestId={`filter-launched-by-${index}`}
              />
            ))}
          {isLaunchedByError && !launchedByList && (
            <Block
              className={css({
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '10px',
                paddingRight: '10px',
              })}
            >
              There was a problem loading Launched By list. Please try again
              later.
            </Block>
          )}
        </Block>
      )}
      placement={PLACEMENT.rightTop}
      autoFocus={false}
      overrides={{
        Inner: {
          style: {
            backgroundColor: customTheme.colors.white,
            borderBottomLeftRadius: '5px',
            borderBottomRightRadius: '5px',
            borderTopLeftRadius: '5px',
            borderTopRightRadius: '5px',
          },
        },
        Body: {
          style: {
            borderBottomLeftRadius: '5px',
            borderBottomRightRadius: '5px',
            borderTopLeftRadius: '5px',
            borderTopRightRadius: '5px',
            marginTop: '0',
            marginLeft: '25px',
          },
        },
      }}
    >
      <Block
        overrides={{
          Block: {
            style: {
              width: '190px',
            },
          },
        }}
      >
        <Button
          onClick={() => {
            filterLaunchedBy?.setFilterOptionLaunchedByClicked(
              FilterMethods.LaunchedBy,
            );
            clearFilterState();
            clearSortingState();
          }}
          value={FilterMethods.LaunchedBy}
          overrides={{
            BaseButton: {
              style: {
                width: '100%',
                display: 'flex',
                color: filterLaunchedBy?.filterOptionLaunchedByClicked
                  ? '#3693BF'
                  : 'black',
                backgroundColor: filterLaunchedBy?.filterOptionLaunchedByClicked
                  ? '#E1EFF5'
                  : 'transparent',
                fontWeight: filterLaunchedBy?.filterOptionLaunchedByClicked
                  ? 'bolder'
                  : 'inherit',
                textAlign: 'center',
                fontSize: '12px',
                outline: 'none !important',
                justifyContent: 'left',
                ':hover': {
                  backgroundColor: 'none',
                },
                ':active': {
                  backgroundColor: 'none',
                },
              },
              props: {
                'data-testid': 'filter-launched-by-btn',
              },
            },
          }}
          endEnhancer={() => <ChevronRight />}
          startEnhancer={() =>
            filterLaunchedBy?.channelsFilterOptions.launched_by.length ? (
              <Check size={'30px'} color="#3693BF" />
            ) : (
              ''
            )
          }
        >
          {FilterMethods.LaunchedBy}
        </Button>
      </Block>
    </StatefulPopover>
  );
};
